/**
 * Every /overview box's state from the page's reads and filters, in one pure
 * function (spec §3.3, §5). The page, the CSV and the chat all read this, so
 * the three cannot disagree. Filters apply here, on the client, with no read
 * (spec §2.2). A failed read fails the boxes it feeds and no other (R16);
 * NULL never becomes 0; filters that leave nothing give "empty".
 *
 * @module services/overview/overviewView
 */
import type { CoverageState } from '@/hooks/useLookerCoverage';
import type { MetricGap } from '@/services/looker/lookerCoverage';
import { badgeCoverageOf } from '@/services/looker/lookerTileNotes';
import type { TileTargets } from '@/services/looker/lookerTypes';
import { isChannelShown } from '@/services/channelFilter';
import { soleChannelLabel } from '@/services/channels/channelFormat';
import type { NoSpendEmpty } from '@/services/channels/channelTypes';
import { metricFor, type AwarenessMetric, type ResultMappingInfo } from '@/services/targets/resultMetric';
import type { CprTargetRow, TargetObjective } from '@/services/targets/targetRules';
import { accountRowsOf, channelRowsOf, type MetricRow } from './breakdownTables';
import { channelShareOf, type ChannelShare } from './channelShare';
import { asTargetObjective, objectiveRowsOf, resultCardOf, type ObjectiveRow, type ResultCardFigures } from './objectiveResults';
import { currencyChoiceOf, filterFacts } from './overviewFilters';
import { groupRows, totalsOf } from './overviewMath';
import { overviewTargetsOf, type ObjectiveCpr } from './overviewTargets';
import type { BoxState, CurrencyChoice, FactPair, FactRow, ObjectiveFilter, OverviewWindows, ReadState } from './overviewTypes';
import { rowsIn } from './overviewWindows';
import { metricValueOf } from './reportedMetric';
import { scorecardsOf, type Scorecard } from './scorecards';
import { trendOf, type Trend } from './spendTrend';
import { topCampaignsOf, unreportedKey, type CampaignRow, type SummaryReads } from './topCampaigns';

export interface OverviewInput {
  windows: OverviewWindows;
  facts: ReadState<FactPair>;
  summary: ReadState<SummaryReads>;
  /** null = still loading. */
  mapping: ResultMappingInfo | null;
  /** idle = all workspaces (targets are workspace-bound, R10). */
  targets: ReadState<CprTargetRow[]>;
  coverage: CoverageState;
  /** Ad channels the workspace syncs (freshness); [] when unknown. */
  connected: readonly string[];
  /**
   * The one ticked ad channel is connected (soleChannelConnectedOf, rules-04 G3); null = unknown: several channels or
   * none ticked, all workspaces, or the statuses read loading or failed. Only the card reads it: false puts it first,
   * before the facts, as on D2 (rules-04 H3).
   */
  channelConnected: boolean | null;
  /** Raw channel ids; [] = every channel. */
  channels: readonly string[];
  objective: ObjectiveFilter;
  /** The viewer's currency pick; null = the default (the most spent). */
  currency: string | null;
}

export interface ResultCard extends ResultCardFigures {
  /** Platforms that spent but do not report this objective's Result metric (orange chips). */
  gaps: MetricGap[];
  /** null = all workspaces: the line asks for a workspace. */
  targets: TileTargets | null;
}

export interface ObjectiveTable {
  rows: ObjectiveRow[];
  /** objective → platforms that do not report its Result metric. */
  gaps: Record<string, MetricGap[]>;
  /** null = all workspaces: the target columns ask for a workspace. */
  targets: TileTargets | null;
}

export interface OverviewView {
  money: CurrencyChoice | null;
  latestFactDate: string | null;
  /**
   * Rule 04.3, as D2's E: no row of the channels in view spent in the period (checked before the objective and
   * currency filters), so the grid draws one card and every facts box below is "empty". `channels` = the ticked
   * ids, [] = every channel. notConnected when channelConnected is false (rules-04 G3), and then first, as D2's E:
   * whatever the facts hold, spend included, and while they load or after they failed (rules-04 H3). Else noSpend;
   * null for the loading and failed states, and while the channels did spend.
   */
  empty: NoSpendEmpty | null;
  scorecards: BoxState<Scorecard[]>;
  /** null = box 3 is not shown (not exactly one of the three objectives). */
  result: BoxState<ResultCard> | null;
  trend: BoxState<Trend>;
  share: BoxState<ChannelShare>;
  objectives: BoxState<ObjectiveTable>;
  channels: BoxState<MetricRow[]>;
  accounts: BoxState<MetricRow[]>;
  campaigns: BoxState<CampaignRow[]>;
}

const LOADING = { status: 'loading' } as const;
const FAILED = { status: 'failed' } as const;
const EMPTY = { status: 'empty' } as const;
const ready = <T>(data: T): BoxState<T> => ({ status: 'ready', data });

const notReady = (input: OverviewInput, state: typeof LOADING | typeof FAILED): OverviewView => ({
  money: null, latestFactDate: null, empty: null,
  scorecards: state, result: asTargetObjective(input.objective) ? state : null, trend: state, share: state,
  objectives: state, channels: state, accounts: state, campaigns: state,
});

/**
 * Every facts box is "empty" and the card speaks (never ready zeros); money and the data date stay the facts', null
 * while they load or after they failed (only "not connected" comes before them, rules-04 H3). The card shows no
 * figure, so none leaves an account of unknown currency out: no flag under either card (rules-04 H3, I2).
 */
const cardView = (
  input: OverviewInput, kind: NoSpendEmpty['kind'], money: CurrencyChoice | null, latestFactDate: string | null,
): OverviewView => ({
  money: money && { ...money, unknownExcluded: false }, latestFactDate,
  empty: { kind, channels: [...input.channels] },
  scorecards: EMPTY, result: asTargetObjective(input.objective) ? EMPTY : null, trend: EMPTY, share: EMPTY,
  objectives: EMPTY, channels: EMPTY, accounts: EMPTY, campaigns: EMPTY,
});

/**
 * One (platform, objective) pair the facts show not reporting its Result metric, as a coverage chip. Its
 * `gap.spend` is the cut window's spend in the page currency; coverage's is the whole period's across every
 * currency, so the two are never added.
 */
interface FactsGap { objective: TargetObjective; gap: MetricGap }

/**
 * F4: (platform, objective) pairs whose cut rows with spend > 0 all hold no value for
 * the metric the mapping selects (metricValueOf: NULL, or a reach the platform did not
 * send). get_campaign_summary COALESCEs those to 0, coverage is null for all workspaces,
 * loading or failed, and get_metric_coverage counts a stored 0 as reported (a reach of
 * 0 only on a row with no impressions, since migration 126, rules-04 G2), so the facts
 * decide too: box 13's Result, and (rules-04 C1) the chips of boxes 3 and 9.
 */
const unreportedInFacts = (cut: readonly FactRow[], awareness: AwarenessMetric | null): FactsGap[] => {
  if (awareness === null) return [];
  const spent = cut.flatMap((r) => {
    const o = asTargetObjective(r.objective);
    return o !== null && (r.spend ?? 0) > 0 ? [{ r, o }] : [];
  });
  return groupRows(spent, ({ r, o }) => unreportedKey(r.platform, o))
    .filter(([, rows]) => rows.every(({ r, o }) => metricValueOf(r, metricFor(o, awareness)) === null))
    .map(([, rows]): FactsGap => {
      const { r, o } = rows[0];
      return { objective: o, gap: { platform: r.platform, metric: metricFor(o, awareness), spend: rows.reduce((s, x) => s + (x.r.spend ?? 0), 0) } };
    });
};

/** Coverage's chips, then the facts' for a platform coverage does not name. */
const withFactsGaps = (coverage: readonly MetricGap[], facts: readonly MetricGap[]): MetricGap[] =>
  [...coverage, ...facts.filter((f) => !coverage.some((g) => g.platform === f.platform))];

export const buildOverviewView = (input: OverviewInput): OverviewView => {
  const { facts, windows } = input;
  // rules-04 H3, D2's order: the one ticked ad channel known not to be connected is the card before the facts are
  // looked at; only for the one channel the sentence names, whatever the caller passed (rules-04 G review L1).
  const notConnected = input.channelConnected === false && soleChannelLabel(input.channels) !== null;
  if (notConnected && facts.status !== 'ready') return cardView(input, 'notConnected', null, null);
  if (facts.status === 'failed') return notReady(input, FAILED);
  if (facts.status !== 'ready') return notReady(input, LOADING);

  const money = currencyChoiceOf(facts.data.current, input.currency);
  const latestFactDate = facts.data.current.map((r) => r.date).sort().pop() ?? null;
  if (notConnected) return cardView(input, 'notConnected', money, latestFactDate);
  const selected = new Set(input.channels);
  // Before the objective and currency filters, as D2's E: a row of unknown currency that spent is spend.
  if (!facts.data.current.some((r) => isChannelShown(selected, r.platform) && r.spend !== null && r.spend > 0)) {
    return cardView(input, 'noSpend', money, latestFactDate);
  }
  const f = { channels: input.channels, objective: input.objective, currency: money.currency };
  const current = filterFacts(facts.data.current, f);
  const previous = filterFacts(facts.data.previous, f);
  const cut = rowsIn(current, windows.cut);
  const cutPrevious = rowsIn(previous, windows.cutPrevious);
  const box = <T>(build: () => T): BoxState<T> => (current.length === 0 ? EMPTY : ready(build()));

  const coverage = badgeCoverageOf(input.coverage, input.channels);
  const awareness = input.mapping?.awareness ?? null;
  const factsGaps = unreportedInFacts(cut, awareness);
  const gapsOf = (o: string): MetricGap[] => {
    const t = asTargetObjective(o);
    if (!t) return [];
    return withFactsGaps(coverage ? coverage.resultGaps[t] : [], factsGaps.filter((g) => g.objective === t).map((g) => g.gap));
  };
  const resultWaits = input.mapping === null || input.targets.status === 'loading';
  const targetRows = input.targets.status === 'ready' ? input.targets.data : input.targets.status === 'failed' ? 'failed' as const : null;
  const judged = (pairs: ObjectiveCpr[]): TileTargets | null => (targetRows === null ? null : overviewTargetsOf(pairs, {
    rows: targetRows, awareness, currency: money.currency, channels: input.channels,
    periodStart: windows.current.start, asOf: windows.through,
  }));

  const one = asTargetObjective(input.objective);
  const result: BoxState<ResultCard> | null = one === null ? null : resultWaits ? LOADING : box(() => {
    const figures = resultCardOf(one, current, cut, cutPrevious, awareness);
    return { ...figures, gaps: gapsOf(one), targets: judged([{ objective: one, cpr: figures.costPerResult }]) };
  });

  const objectives: BoxState<ObjectiveTable> = resultWaits ? LOADING : box(() => {
    const rows = objectiveRowsOf(current, cut, cutPrevious, awareness);
    const pairs = rows.flatMap((r): ObjectiveCpr[] => {
      const o = asTargetObjective(r.objective);
      return o ? [{ objective: o, cpr: r.costPerResult }] : [];
    });
    return { rows, gaps: Object.fromEntries(rows.map((r) => [r.objective, gapsOf(r.objective)])), targets: judged(pairs) };
  });

  const connected = input.connected.filter((p) => isChannelShown(selected, p));
  const accountCurrency = new Map(facts.data.current.flatMap((r) => (r.accountId ? [[r.accountId, r.currency] as const] : [])));
  const campaigns: BoxState<CampaignRow[]> = input.summary.status === 'failed' ? FAILED
    : input.summary.status !== 'ready' || input.mapping === null ? LOADING
      : (() => {
        const rows = topCampaignsOf({
          summary: input.summary.data, accountCurrency, channels: input.channels, objective: input.objective,
          currency: money.currency, awareness, coverage, hasCut: windows.cut !== null,
          unreportedFromFacts: new Set(factsGaps.map((g) => unreportedKey(g.gap.platform, g.objective))),
        });
        return rows.length === 0 ? EMPTY : ready(rows);
      })();

  return {
    money,
    latestFactDate,
    empty: null,
    scorecards: box(() => scorecardsOf(totalsOf(current), totalsOf(previous))),
    result,
    trend: box(() => trendOf(current, windows.current)),
    share: box(() => channelShareOf(current, connected)),
    objectives,
    channels: box(() => channelRowsOf(current)),
    accounts: box(() => accountRowsOf(current)),
    campaigns,
  };
};
